import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { confClass } from "./LinesTable";

const OPTIONAL = new Set(["vat_amount", "po_reference", "customer_account_number", "customer_name", "vat_number", "telephone", "email", "address"]);

function Field({ label, k, value, conf, onChange, type = "text", readOnly, hint }) {
  const optional = OPTIONAL.has(k);
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">{label}{conf === "low" && !optional && <span className="ml-1 text-amber-700 font-normal">· check</span>}{optional && (value === null || value === undefined || value === "") && <span className="ml-1 text-slate-400 font-normal">· optional</span>}</Label>
      <Input type={type} step="any" value={value ?? ""} readOnly={readOnly} onChange={(e) => onChange(k, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
        data-testid={`field-${k}`} className={`h-10 ${type === "number" ? "font-mono text-right" : ""} ${optional ? "" : confClass(conf, value)}`} placeholder={hint} />
    </div>
  );
}

export default function HeaderFields({ invoice, supplier, onChange, onSupplierChange, readOnly }) {
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/suppliers", { params: { all: true } }).then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  const conf = invoice.confidence || {};
  const ext = invoice.extracted_supplier || {};
  const set = (k, v) => onChange({ [k]: v });
  const setExt = (k, v) => onChange({ extracted_supplier: { ...ext, [k]: v } });

  return (
    <div className="space-y-5" data-testid="header-fields">
      <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Supplier</h3>
          {invoice.supplier_match === "auto" && supplier && <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="supplier-auto-matched">Auto-matched</span>}
          {invoice.supplier_match === "auto_created" && supplier && <span className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5" data-testid="supplier-auto-created">New supplier created</span>}
          {!invoice.supplier_id && ext.name && <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="supplier-new">New supplier will be created</span>}
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-600">Match to existing supplier</Label>
          <select value={invoice.supplier_id || ""} disabled={readOnly} onChange={(e) => onSupplierChange(e.target.value || null)} data-testid="supplier-select"
            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-70">
            <option value="">— Create new from invoice: {ext.name || "unnamed"} —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {supplier && <Link to={`/suppliers/${supplier.id}`} className="text-xs text-emerald-700 hover:underline">View {supplier.name}</Link>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name on invoice" k="name" value={ext.name} conf={conf.supplier_name} onChange={setExt} readOnly={readOnly} />
          <Field label="VAT number" k="vat_number" value={ext.vat_number} onChange={setExt} readOnly={readOnly} />
          <Field label="Telephone" k="telephone" value={ext.telephone} onChange={setExt} readOnly={readOnly} />
          <Field label="Email" k="email" value={ext.email} onChange={setExt} readOnly={readOnly} />
          <div className="col-span-2"><Field label="Address" k="address" value={ext.address} onChange={setExt} readOnly={readOnly} /></div>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Invoice details</h3>
          <select value={invoice.document_type || "invoice"} disabled={readOnly} onChange={(e) => set("document_type", e.target.value)} data-testid="document-type-select" className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
            <option value="invoice">Invoice</option><option value="credit_note">Credit note</option><option value="delivery_note">Delivery note</option><option value="other">Other</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Invoice number" k="invoice_number" value={invoice.invoice_number} conf={conf.invoice_number} onChange={set} readOnly={readOnly} />
          <Field label="Invoice date" k="invoice_date" type="date" value={invoice.invoice_date} conf={conf.invoice_date} onChange={set} readOnly={readOnly} />
          <Field label="Your account no." k="customer_account_number" value={invoice.customer_account_number} conf={conf.customer_account_number} onChange={set} readOnly={readOnly} />
          <Field label="Customer name" k="customer_name" value={invoice.customer_name} onChange={set} readOnly={readOnly} />
          <Field label="PO / reference" k="po_reference" value={invoice.po_reference} onChange={set} readOnly={readOnly} />
        </div>
        <div className="grid grid-cols-4 gap-3 pt-1">
          <Field label="Net" k="subtotal" type="number" value={invoice.subtotal} conf={conf.subtotal} onChange={set} readOnly={readOnly} />
          <Field label="VAT" k="vat_amount" type="number" value={invoice.vat_amount} conf={conf.vat_amount} onChange={set} readOnly={readOnly} />
          <Field label="Total" k="total" type="number" value={invoice.total} conf={conf.total} onChange={set} readOnly={readOnly} />
          <Field label="Currency" k="currency" value={invoice.currency} onChange={set} readOnly={readOnly} />
        </div>
      </section>
    </div>
  );
}
